import { useEffect, useState } from 'react';
import { Alert, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Page from '../../../components/Page';
import Article from '../../../components/Article';
import CustomButton from '../../../components/CustomButton';
import ThemedTextField from '../../../components/ThemedTextField';
import { useTenant } from '../../../contexts/TenantContext';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { storage } from '../../../services/storageService';
import { StorageKeys } from '../../../services/constants/StorageKeys';
import { Routes } from '../../../routes/RouteConstants';
import AuthLink from '../components/AuthLink';
import useAuthRequest from '../useAuthRequest';
import useAuthNextStep from '../useAuthNextStep';
import { codeRule, newPasswordRule } from '../validation';

// The backend refuses a new code within a minute of the last one (DEC-0021); the button waits as long.
const RESEND_INTERVAL_MS = 60 * 1000;

/**
 * The code of a registration or a password reset. It works only with the token this browser got
 * when it asked (docs/EMAIL_AUTH.md A14), which is why the page is guarded by it. A reset's new
 * password is typed here too, next to the code.
 */
const VerifyEmailLayout = () => {
  const { displayConfig } = useTenant();
  const showSnackbar = useSnackbar();
  const confirm = useAuthRequest("email-verification/confirm");
  const resend = useAuthRequest("email-verification/code");
  const goToNextStep = useAuthNextStep();

  const [emailVerification, setEmailVerification] = useState(() => storage.get(StorageKeys.EMAIL_VERIFICATION));
  const [now, setNow] = useState(Date.now());
  const [confirmFailed, setConfirmFailed] = useState(false);
  const isReset = emailVerification.purpose === "PASSWORD_RESET";

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const secondsUntilResend = Math.max(0, Math.ceil((emailVerification.sentAt + RESEND_INTERVAL_MS - now) / 1000));

  const formik = useFormik({
    initialValues: {
      code: '',
      newPassword: '',
    },
    validationSchema: yup.object(isReset
      ? { code: codeRule, newPassword: newPasswordRule }
      : { code: codeRule }),
    onSubmit: async ({ code, newPassword }) => {
      const authResponse = await confirm.send({
        token: emailVerification.token,
        code: code.trim(),
        ...(isReset ? { newPassword } : {}),
      });
      if (!authResponse) {
        setConfirmFailed(true);
      }
      goToNextStep(authResponse);
    },
  });

  const handleResend = async () => {
    const sent = await resend.send({ token: emailVerification.token });
    if (!sent) {
      return;
    }

    const updated = { ...emailVerification, sentAt: Date.now() };
    storage.set(StorageKeys.EMAIL_VERIFICATION, updated);
    setEmailVerification(updated);
    formik.setFieldValue("code", "");
    showSnackbar(isReset ? "Ha ehhez a címhez tartozik fiók, újra elküldtük rá a kódot." : "Újra elküldtük a kódot.", "success");
  };

  // The way out of a reset that cannot work: no account, a registration deleted meanwhile, a code
  // that never comes. A registration with the same address always ends in a working account (A7).
  // Only after a failed confirmation does it stand out; the snackbar still says what went wrong.
  const registerAgainHint = (
    <>
      Ha nem kapsz kódot, vagy nem sikerül új jelszót beállítani, <AuthLink to={Routes.REGISTER}>regisztrálj újra</AuthLink> – akár ugyanezzel az email címmel.
    </>
  );

  return (
    <Page>
      <Article
        xsHorizontalPadding={2}
        title={isReset ? "Új jelszó beállítása" : "Email cím megerősítése"}
        titleVariant="h5"
      >
        <Typography variant="body1" color={displayConfig?.color} mb={3} textAlign="center">
          {isReset
            ? <>Ha a(z) <strong>{emailVerification.email}</strong> címhez tartozik fiók, elküldtük rá a 6 jegyű kódot. Add meg a kódot és az új jelszavadat.</>
            : <>Elküldtük a 6 jegyű kódot a(z) <strong>{emailVerification.email}</strong> címre. Írd be itt, ebben a böngészőben.</>}
          {" "}A kód 15 percig érvényes.
        </Typography>

        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }} noValidate>
          <Stack spacing={3}>
            <ThemedTextField
              id="code"
              name="code"
              label="Kód"
              autoComplete="one-time-code"
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
              value={formik.values.code}
              onChange={formik.handleChange}
              error={formik.touched.code && Boolean(formik.errors.code)}
              helperText={formik.touched.code && formik.errors.code}
            />
            {isReset &&
              <ThemedTextField
                id="newPassword"
                name="newPassword"
                label="Új jelszó"
                type="password"
                autoComplete="new-password"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                helperText={(formik.touched.newPassword && formik.errors.newPassword) || "Legalább 8 karakter"}
              />
            }
            {isReset && confirmFailed &&
              <Alert severity="warning">{registerAgainHint}</Alert>
            }
            <CustomButton
              text={isReset ? "Jelszó beállítása" : "Megerősítés"}
              textTransform="none"
              type="submit"
              isLoading={confirm.loading}
            />
            <CustomButton
              variant="outlined"
              text={secondsUntilResend > 0 ? `Új kód küldése (${secondsUntilResend} mp)` : "Új kód küldése"}
              textTransform="none"
              onClick={handleResend}
              isLoading={resend.loading}
              disabled={secondsUntilResend > 0}
            />
          </Stack>
        </form>

        {isReset && !confirmFailed &&
          <Typography variant="body2" color={displayConfig?.color} mt={3} textAlign="center">
            {registerAgainHint}
          </Typography>
        }

        <Typography variant="body2" color={displayConfig?.color} mt={3} textAlign="center">
          <AuthLink to={isReset ? Routes.FORGOT_PASSWORD : Routes.REGISTER}>Másik email címet adok meg</AuthLink>
        </Typography>
      </Article>
    </Page>
  );
};

export default VerifyEmailLayout;
